import React, { Component } from 'react';
import styled, { ThemeProvider, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { fromJS, Map, List } from 'immutable';
import PropTypes from 'prop-types';
import { Flex, Box } from 'grid-styled';
import { color, size } from '../styles/helpers/variables';
import { Container } from '../styles/Container';
import connect from '../store/connect';
import { Conditional } from '../components/Conditional';
import { fetchArticlePageView } from '../actions/actionCreators';
import { showOnPrint } from '../styles/helpers/mixins';
import { NEWSLETTER_PAGE_TYPE } from '../constants/pageTypes';

class Footer extends Component {
  componentDidMount() {
    this.props.fetchArticlePageView({ urlPath: '/footer' });
  }

  desktopLinkListRender() {
    return this.props.footerLinks
      .map((item, index) => {
        const children = item.get('children');
        let itemsWithoutChildren = [];

        if (!children.size) {
          return false;
        }

        let indexOfNextItemWithChildren = 0;
        this.props.footerLinks.forEach((subItem, subIndex) => {
          if (subIndex > index && subItem.get('children').size) {
            // update indexOfNextItemWithChildren
            indexOfNextItemWithChildren = subIndex;
          }
          if (subIndex > index && index > indexOfNextItemWithChildren && !subItem.get('children').size) {
            itemsWithoutChildren.push(subItem);
          }
        });

        const CurrentBox = (
          <Box width={[1 / 5]} px={[0, '14px']} key={index}>
            <FooterLinks>
              <FooterLinkTitleItem>
                <FooterDetailTitleLink to={item.get('url')}>{item.get('title')}</FooterDetailTitleLink>
              </FooterLinkTitleItem>
              {item.get('children').map((child, childIndex) => (
                <FooterLinkItem key={childIndex}>
                  <FooterDetailLink to={child.get('url')}>{child.get('title')}</FooterDetailLink>
                </FooterLinkItem>
              ))}
              <Conditional show={!!itemsWithoutChildren.length}>
                {itemsWithoutChildren.map(savedItem => (
                  <FooterLinkTitleItem marginTop key={savedItem.get('url')}>
                    <FooterDetailTitleLink to={savedItem.get('url')}>{savedItem.get('title')}</FooterDetailTitleLink>
                  </FooterLinkTitleItem>
                ))}
              </Conditional>
            </FooterLinks>
          </Box>
        );

        itemsWithoutChildren = [];
        return CurrentBox;
      })
      .filter(item => !!item)
      .toArray();
  }

  render() {
    const { viewstate, sitemap } = this.props;
    const isMobile = viewstate.get('pageWidth') < size.footerBreakPoint;
    const mobileTheme = {
      isMobile: true
    };
    const desktopTheme = {
      isMobile: false
    };

    const newsletterLink = sitemap
      .get('children', Map())
      .filter(item => item.get('pageType') === NEWSLETTER_PAGE_TYPE)
      .map(item => (
        <SocialLinkWithLabelAsLink to={item.get('url')} key={item.get('url')}>
          Premenurera på nyhetsbrev
          <SocialIcon>
            <use href="/images/icons/social_v1.svg#newsletter" />
          </SocialIcon>
        </SocialLinkWithLabelAsLink>
      ))
      .toArray();

    const socialLinks = (
      <SocialLinks>
        <SocialLinksLabel>Följ oss</SocialLinksLabel>
        {[
          {
            id: 'facebook',
            href: 'https://www.facebook.com/ValaMagasinet/'
          },
          {
            id: 'instagram',
            href: 'https://www.instagram.com/valamagasinet/'
          }
        ].map(({ id, href }) => (
          <SocialLink href={href} target="_blank" rel="noreferrer">
            <SocialIcon>
              <use href={`/images/icons/social_v1.svg#${id}`} />
            </SocialIcon>
          </SocialLink>
        ))}
      </SocialLinks>
    );

    if (isMobile) {
      return (
        <ThemeProvider theme={mobileTheme}>
          <Container hideOnPrint style={{ padding: '40px 20px' }}>
            <MobileLinkList>
              {this.props.footerLinksMobile.map(item => (
                <MobileLinkListItem>
                  <MobileLink to={item.get('url')}>{item.get('title')}</MobileLink>
                </MobileLinkListItem>
              ))}
            </MobileLinkList>
            <SocialContainer>
              {socialLinks}
              {newsletterLink}
            </SocialContainer>
            <FooterDetails center>
              <CopyText mobile>&copy; Vålamagasinet. Alla rättigheter förbehålls.</CopyText>
            </FooterDetails>
          </Container>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={desktopTheme}>
        <FooterContainer>
          <PrintFooter>
            {this.props.contactInformation.map((item, index) => (
              <li key={`print-contact-${index}`} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </PrintFooter>

          <Container swipe hideOnPrint>
            <SocialDesktopContainer>
              {newsletterLink}
              <SocialDesktopContainerDivider />
              {socialLinks}
            </SocialDesktopContainer>
            <Flex mx={[0, '-14px']} mb={30}>
              <Box width={[1 / 5]} px={[0, '14px']}>
                <FooterLinks>
                  {this.props.contactInformation.map((item, index) => (
                    <FooterItem
                      title={index === 0 ? 'title' : undefined}
                      className={index === 3 ? '--mb' : undefined}
                      key={index + 1}
                    >
                      <div dangerouslySetInnerHTML={{ __html: item }} />
                    </FooterItem>
                  ))}
                </FooterLinks>
              </Box>
              <Box width={[1 / 5]} px={[0, '14px']}>
                <FooterLinks>
                  {this.props.openingHours.map((item, index) => (
                    <FooterItem title={index === 0 ? 'title' : undefined} key={index + 1}>
                      {item}
                    </FooterItem>
                  ))}
                </FooterLinks>
              </Box>
              {this.desktopLinkListRender()}
            </Flex>
            <FooterDetails>
              <CopyText>&copy; Vålamagasinet. Alla rättigheter förbehålls.</CopyText>
            </FooterDetails>
          </Container>
        </FooterContainer>
      </ThemeProvider>
    );
  }
}

const PrintFooter = styled.ul`
  ${showOnPrint};
  list-style: none;
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;

  li {
    font-size: 12px;
    &:first-child {
      display: none;
    }
  }
`;

const FooterDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => (props.center ? 'center' : 'flex-start')};
`;

const SocialContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 0 40px;
  > div {
    &:first-child {
      margin: 0 0 20px;
    }
  }
`;

const SocialDesktopContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 40px;
`;
const SocialDesktopContainerDivider = styled.div`
  flex: 1 1 auto;
  height: 1px;
  margin: 0 10px;
  background-color: #363636;
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
`;

const SocialLinksLabel = styled.div`
  margin: 0 10px 0 0;
  color: ${color.white};
  font-size: 11px;
  font-weight: 600;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  @media screen and (min-width: 850px) {
    font-size: 13px;
  }
`;

const SocialLink = styled.a`
  margin: 0 10px 0 0;
  &:last-child {
    margin: 0;
  }
`;

const SocialLinkWithLabel = styled.a`
  display: flex;
  align-items: center;
  color: ${color.white};
  font-size: 11px;
  font-weight: 600;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: 0.15em;
  text-decoration: none;
  text-transform: uppercase;
  @media screen and (min-width: 850px) {
    font-size: 13px;
  }
  > svg {
    flex: 0 0 auto;
    margin: 0 0 0 10px;
  }
`;

const SocialIcon = styled.svg`
  display: block;
  height: 30px;
  width: 30px;
`;

const SocialLinkWithLabelAsLink = SocialLinkWithLabel.withComponent(Link);

const MobileLinkList = styled.ul`
  list-style: none;
  margin: 0 0 40px;
  padding: 0 0 40px;
  border-bottom: 1px solid #363636;
  line-height: 1.25;
  text-align: center;
`;
const MobileLinkListItem = styled.li`
  margin: 0 0 20px;
  &:last-child {
    margin: 0;
  }
`;
const MobileLink = styled(Link)`
  color: ${color.white};
  font-family: PlayfairDisplay;
  font-size: 14px;
  text-decoration: none;
`;

const FooterDetailLink = styled(Link)`
  color: ${color.white};
  text-decoration: none;
  padding: 2px 0;
  transition: opacity 250ms ease-out;
  position: relative;

  &:before,
  :after {
    position: absolute;
    content: '';
    display: block;
    width: 6px;
    height: 1px;
    background: ${color.white};
    right: -12px;
  }
  &:before {
    top: calc(50% - 1px);
    transform: rotate(40deg);
  }
  &:after {
    top: calc(50% + 3px);
    transform: rotate(-40deg);
  }

  &:hover {
    opacity: 0.8;
  }
`;

const FooterDetailTitleLink = styled(Link)`
  color: ${color.white};
  text-decoration: none;
  padding: 2px 0;
  transition: opacity 250ms ease-out;
  position: relative;
  font-family: PlayfairDisplay;
  font-size: 24px;
  line-height: 1.25;
  text-align: left;

  &:hover {
    opacity: 0.8;
  }
`;

const CopyText = styled.span`
  color: ${color.white};
  display: block;
  font-size: 12px;
  line-height: 2.08;
  text-align: left;
  font-style: italic;
  ${props =>
    props.mobile &&
    css`
      font-size: 10px;
    `};
`;

const FooterLinks = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  padding-left: 0;
  margin: 0 0 20px;
  color: ${color.white};
`;

const FooterLinkItem = styled.li`
  color: ${props => (props.theme ? props.theme.fg : color.white)};
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 5px;
  text-align: left;
  padding: 0;
  font-weight: normal;
`;

const FooterLinkTitleItem = styled.li`
  color: ${props => (props.theme ? props.theme.fg : color.white)};
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 8px;
  text-align: left;
  padding: 0;
  font-weight: normal;

  ${props =>
    props.marginTop &&
    css`
      margin-top: 8px;
      margin-bottom: 0;

      ${FooterLinkItem} + & {
        margin-top: 20px;
      }
    `};
`;

const FooterItem = styled.li`
  color: ${color.white};
  font-size: 14px;
  text-decoration: none;
  line-height: 1.5;
  margin-bottom: 5px;
  text-align: left;
  padding: 0;
  font-weight: normal;

  ${props =>
    props.title &&
    css`
      margin-bottom: 8px;
      font-family: PlayfairDisplay;
      font-size: 24px;
      line-height: 1.25;
      text-align: left;
    `};

  &.--mb {
    margin-bottom: 1.5em;
  }
`;

const FooterContainer = styled.footer`
  height: auto;
  display: flex;
  padding: 80px 0;
  justify-content: center;
  align-items: center;
  background-color: ${color.black};
  ul {
    padding: 0;
    list-style: none;
  }
`;

Footer.defaultProps = {
  viewstate: Map(),
  footerLinks: List(),
  footerLinksMobile: List(),
  sitemap: Map(),
  openingHours: List(),
  contactInformation: List()
};

Footer.propTypes = {
  viewstate: PropTypes.instanceOf(Map),
  footerLinks: PropTypes.instanceOf(List),
  footerLinksMobile: PropTypes.instanceOf(List),
  sitemap: PropTypes.instanceOf(Map),
  fetchArticlePageView: PropTypes.func.isRequired,
  openingHours: PropTypes.instanceOf(List),
  contactInformation: PropTypes.instanceOf(List)
};

export default connect(Footer, { fetchArticlePageView }, store => ({
  viewstate: store.viewstate,
  footerLinks: fromJS((window.startup || {}).footerLinks),
  footerLinksMobile: fromJS((window.startup || {}).mobileFooterLinks),
  sitemap: fromJS(window.sitemap),
  openingHours: store.page.data.getIn(['/footer', 'fields', 'openingHoursArray'], List()),
  contactInformation: store.page.data.getIn(['/footer', 'fields', 'contactInformationArray'], List())
}));



// WEBPACK FOOTER //
// ./src/components/Footer.js